import { useMemo } from 'react';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { BANK_MOODS, type BankMood } from 'fym-shared/src/music24/reelBankTaxonomy';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { useGetFootageBankSuggestQuery } from '../musicVideoEditorApi';
import { bankFootageView, type FootageView } from './footageView';
import { useBankSearch } from './useBankSearch';

/** The clips of one "For <section>" list (the suggest route answers 12). */
export const SECTION_BANK_CLIPS = 12;

/** The bank mood of a section, or undefined for a mood the bank does not know. Pure. */
export function sectionBankMood(section: Pick<MusicVideoSongMapSection, 'mood'> | null): BankMood | undefined {
  const mood = section?.mood ?? null;
  return mood !== null && (BANK_MOODS as readonly string[]).includes(mood) ? (mood as BankMood) : undefined;
}

/**
 * The bank clips for a section ("For <section>", "Replace with a similar clip", the phone picker).
 * Account mode: the clips the director would pick (`GET /footage/bank/suggest`). Showcase mode has no
 * suggest route: the public bank search with the mood of the section (one cached read per mood).
 */
export function useSectionBankClips(section: MusicVideoSongMapSection | null) {
  const { mode, accountId, catalogItemUuid } = useMusicVideoEditor();
  const isShowcase = mode === 'showcase';
  const suggest = useGetFootageBankSuggestQuery(
    { accountId, catalogItemUuid, section: section?.index ?? 0 },
    { skip: isShowcase || section === null }
  );
  const search = useBankSearch(
    { mood: sectionBankMood(section), limit: SECTION_BANK_CLIPS },
    { skip: !isShowcase || section === null }
  );
  const items = isShowcase ? search.data?.items : suggest.data?.items;
  const views = useMemo<FootageView[]>(() => (items ?? []).map(bankFootageView), [items]);
  return {
    views,
    isLoading: isShowcase ? search.isLoading : suggest.isLoading,
    error: isShowcase ? search.error : suggest.error,
    hasData: items !== undefined,
    isBusy: search.isBusy,
    retry: () => {
      if (isShowcase) {
        search.retry();
        return;
      }
      suggest.refetch().catch(console.error);
    },
  };
}
